/**
 * «Директор → Воронка аукционов» (ВОРОНКА). Аналитика продаж по тендерам:
 * воронка по стадиям с конверсией, плитки цен, исход, разрезы. Данные —
 * GET /api/tenders/funnel (owner-гейт). См. plans/2026-10-19-tender-funnel.md.
 */
import { api } from '../api.js';
import { esc, fmtMoney, isOwner } from '../utils/tenders.js';

const PERIODS = [
  { value: 'month', label: 'Месяц' },
  { value: 'quarter', label: 'Квартал' },
  { value: 'all', label: 'Всё время' },
];

// Дип-линки плиток исхода ведут туда, где счётчик = список (чип снимет «кроме закрытых»).
// «Отказ наш» (decision=no_go ИЛИ rejected) честного списочного фильтра не имеет — без ссылки.
const OUTCOME_LINK = {
  won: '/tenders?status=won,guarantee,contract',
  lost: '/tenders?status=lost',
  customer_refused: '/tenders?status=customer_refused',
};

const BREAKDOWNS = [
  { key: 'by_month', title: 'По месяцам', head: 'Месяц' },
  { key: 'by_customer', title: 'По заказчикам', head: 'Заказчик' },
  { key: 'by_region', title: 'По регионам', head: 'Регион' },
  { key: 'by_work_class', title: 'По классу работ', head: 'Класс' },
];

const _state = { period: 'all', by: 'created_at' };

export async function initTenderFunnel(container) {
  if (!isOwner()) {
    container.innerHTML = `<div class="stub-view"><div class="stub-card">
      <h2 class="stub-title">Воронка аукционов</h2>
      <p class="stub-text">Раздел доступен только владельцу.</p></div></div>`;
    return;
  }
  container.innerHTML = `
    <div class="funnel-root">
      <div class="funnel-toolbar dt-toolbar">
        <div class="dt-chip-group">
          <span class="dt-chip-label">Период</span>
          ${PERIODS.map(p => `<button class="dt-chip" data-period="${p.value}">${p.label}</button>`).join('')}
        </div>
      </div>
      <div class="funnel-body"><div class="loading-spinner">Загрузка…</div></div>
    </div>`;

  const toolbar = container.querySelector('.funnel-toolbar');
  const body = container.querySelector('.funnel-body');
  toolbar.addEventListener('click', e => {
    const btn = e.target.closest('[data-period]');
    if (!btn || btn.dataset.period === _state.period) return;
    _state.period = btn.dataset.period;
    syncChips(toolbar);
    load(body);
  });
  syncChips(toolbar);
  await load(body);
}

function syncChips(toolbar) {
  toolbar.querySelectorAll('[data-period]').forEach(b =>
    b.classList.toggle('is-on', b.dataset.period === _state.period));
}

async function load(body) {
  body.innerHTML = '<div class="loading-spinner">Загрузка…</div>';
  try {
    const d = await api.tenders.funnel({ period: _state.period, by: _state.by });
    body.innerHTML = render(d);
  } catch (e) {
    body.innerHTML = `<div class="funnel-error">Не удалось загрузить воронку: ${esc(e.message)}</div>`;
  }
}

function render(d) {
  return `
    ${renderMetrics(d.prices)}
    <section class="funnel-section">
      <h3 class="funnel-h">Воронка</h3>
      ${renderFunnel(d.funnel)}
    </section>
    <section class="funnel-section">
      <h3 class="funnel-h">Исход</h3>
      ${renderOutcomes(d.outcomes)}
    </section>
    ${BREAKDOWNS.map(b => renderBreakdown(b.title, b.head, d[b.key])).join('')}`;
}

function fmtPct(v) {
  return v == null ? '—' : `${v}%`;
}

function renderMetrics(p) {
  const tiles = [
    ['Средний НМЦК', fmtMoney(p.avg_initial_price), 'по поданным'],
    ['Средняя цена участия', fmtMoney(p.avg_price_1), 'по поданным'],
    ['Среднее снижение', fmtPct(p.avg_discount_pct), 'цена участия к НМЦК'],
    ['Win-rate', fmtPct(p.win_rate_pct), 'выиграно / подано'],
    ['Сумма выигранных', fmtMoney(p.won_sum), 'итог торгов'],
    ['Средний чек', fmtMoney(p.won_avg), 'итог торгов'],
  ];
  return `<section class="funnel-metrics">
    ${tiles.map(([k, v, h]) => `<div class="funnel-tile">
      <div class="funnel-tile-value">${esc(v)}</div>
      <div class="funnel-tile-key">${esc(k)}</div>
      <div class="funnel-tile-hint">${esc(h)}</div>
    </div>`).join('')}
  </section>`;
}

function renderFunnel(stages) {
  const base = stages[0]?.count || 1;   // % от «Загружено»; ширину клампим в [0,100]
  return `<div class="funnel-bars">
    ${stages.map(s => {
      const w = Math.max(0, Math.min(100, s.count / base * 100));
      return `<div class="funnel-bar-row">
        <div class="funnel-bar-label">${esc(s.label)}</div>
        <div class="funnel-bar-track">
          <div class="funnel-bar-fill" style="width:${w}%"></div>
          <span class="funnel-bar-count">${s.count}</span>
        </div>
        <div class="funnel-bar-conv" title="Конверсия к предыдущей стадии">${
          s.conversion_pct == null ? '' : `${s.conversion_pct}%`
        }</div>
      </div>`;
    }).join('')}
  </div>`;
}

function renderOutcomes(outcomes) {
  return `<div class="funnel-outcomes">
    ${outcomes.map(o => {
      const link = OUTCOME_LINK[o.key];
      const cls = `funnel-tile funnel-outcome funnel-outcome--${o.key}${link ? ' is-link' : ''}`;
      const attr = link ? ` data-route="${link}"` : '';
      return `<div class="${cls}"${attr}>
        <div class="funnel-tile-value">${o.count}</div>
        <div class="funnel-tile-key">${esc(o.label)}</div>
      </div>`;
    }).join('')}
  </div>`;
}

function renderBreakdown(title, head, rows) {
  if (!rows || !rows.length) {
    return `<section class="funnel-section">
      <h3 class="funnel-h">${esc(title)}</h3>
      <div class="funnel-empty">Нет данных за период</div>
    </section>`;
  }
  return `<section class="funnel-section">
    <h3 class="funnel-h">${esc(title)}</h3>
    <div class="dt-scroll funnel-table-wrap">
      <table class="dt-table funnel-table">
        <thead><tr>
          <th>${esc(head)}</th>
          <th class="num">Загружено</th>
          <th class="num">Подано</th>
          <th class="num">Выиграно</th>
          <th class="num">Проиграно</th>
          <th class="num">Сумма выигранных</th>
        </tr></thead>
        <tbody>
          ${rows.map(r => `<tr>
            <td>${esc(r.label)}</td>
            <td class="num">${r.loaded}</td>
            <td class="num">${r.submitted}</td>
            <td class="num">${r.won}</td>
            <td class="num">${r.lost}</td>
            <td class="num">${esc(fmtMoney(r.won_sum))}</td>
          </tr>`).join('')}
        </tbody>
      </table>
    </div>
  </section>`;
}
